import type { GlossaryConcept } from "@/lib/glossary-autolink";
import type { ArticleSource } from "@/lib/article-enhancements";
import type { FaqPair } from "@/lib/faq-extract";
import { CiteButton } from "@/components/CiteButton";

type Lang = "tr" | "en";

interface Props {
  lang: Lang;
  summary: string[];
  takeaways: string[];
  learning: string[];
  concepts: GlossaryConcept[];
  sources: ArticleSource[];
  /** Approved-sources mode: the cite block never claims to list sources. */
  citeOnly?: boolean;
  faq: FaqPair[];
  citation?: { title: string; slug: string; isoDate?: string };
}

const L = {
  tr: {
    analysis: "Analiz Özeti ve Çıkarımlar",
    analysisHint:
      "Yazının kısa özeti ve okurun bu metinden çıkarması gereken temel önermeler.",
    summarySub: "Özet",
    takeawaysSub: "Temel Çıkarımlar",
    learning: "Bu Yazıda Öğrenecekleriniz",
    learningHint: "Bu yazıyı okuyunca aşağıdaki soruların cevaplarına sahip olacaksınız.",
    concepts: "Ana Kavramlar",
    conceptsHint: "Yazıda geçen kavramların sözlük tanımları.",
    sources: "Kaynakça ve Atıf",
    sourcesHint: "Yazıda kullanılan raporlar ve akademik kaynaklar.",
    faq: "Bu Yazı Hangi Soruya Cevap Veriyor?",
  },
  en: {
    analysis: "Analysis Summary & Takeaways",
    analysisHint:
      "A concise summary of the essay and the propositions a reader should leave with.",
    summarySub: "Summary",
    takeawaysSub: "Key Takeaways",
    learning: "What You'll Learn",
    learningHint: "After reading, you'll be able to answer these questions.",
    concepts: "Key Concepts",
    conceptsHint: "Glossary definitions for the concepts used in this essay.",
    sources: "References & Citation",
    sourcesHint: "Reports and academic sources used in this essay.",
    faq: "What Questions Does This Essay Answer?",
  },
} as const;

function SectionHeader({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="mb-6">
      <h2 className="font-serif text-2xl md:text-3xl italic tracking-tight text-balance">
        {title}
      </h2>
      {hint && <p className="mt-2 text-sm text-ink/60 leading-relaxed">{hint}</p>}
    </div>
  );
}

export function ArticleEnhancements({
  lang,
  summary,
  takeaways,
  learning,
  concepts,
  sources,
  citeOnly = false,
  faq,
  citation,
}: Props) {
  const t = L[lang];
  const hasAnalysis = summary.length > 0 || takeaways.length > 0;
  const hasAny =
    hasAnalysis ||
    learning.length > 0 ||
    concepts.length > 0 ||
    sources.length > 0 ||
    faq.length > 0;
  if (!hasAny) return null;

  return (
    <div className="mt-16 pt-10 border-t border-border space-y-14">
      {/* --- Combined analysis + key takeaways --- */}
      {hasAnalysis && (
        <section
          aria-labelledby="art-analysis"
          className="-mx-6 md:mx-0 px-6 md:px-8 py-8 md:py-10 bg-moss/5 border-y md:border md:border-border md:rounded-sm"
        >
          <div id="art-analysis">
            <SectionHeader title={t.analysis} hint={t.analysisHint} />
          </div>

          {summary.length > 0 && (
            <div className="mb-8">
              <div className="text-[10px] uppercase tracking-[0.25em] text-clay font-bold mb-4">
                {t.summarySub}
              </div>
              <ul className="space-y-3 list-none pl-0">
                {summary.map((b, i) => (
                  <li
                    key={i}
                    className="relative pl-6 text-base md:text-lg leading-relaxed text-ink/85"
                  >
                    <span
                      aria-hidden
                      className="absolute left-0 top-[0.65em] h-1.5 w-1.5 rounded-full bg-clay"
                    />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {takeaways.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-clay font-bold mb-4">
                {t.takeawaysSub}
              </div>
              <ol
                className="space-y-4 list-none pl-0"
                style={{ counterReset: "takeaway" }}
              >
                {takeaways.map((it, i) => (
                  <li
                    key={i}
                    className="relative pl-10 text-base md:text-lg leading-relaxed text-ink/90 font-serif"
                  >
                    <span
                      aria-hidden
                      className="absolute left-0 top-0 font-serif italic text-clay text-lg"
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {it}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </section>
      )}

      {/* --- Concepts --- */}
      {concepts.length > 0 && (
        <section aria-labelledby="art-concepts">
          <SectionHeader title={t.concepts} hint={t.conceptsHint} />
          <ul id="art-concepts" className="flex flex-wrap gap-2">
            {concepts.map((c) => (
              <li key={c.id}>
                <a
                  href={c.href}
                  className="inline-flex items-center gap-1 border border-ink/20 px-3 py-1.5 text-[11px] md:text-xs uppercase tracking-[0.18em] hover:border-clay hover:text-clay transition-colors"
                >
                  {c.label}
                  <span aria-hidden className="text-clay">↗</span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* --- Sources + Cite --- */}
      {(sources.length > 0 || citation) && (
        <section aria-labelledby="art-sources">
          {sources.length === 0 && citeOnly ? (
            <SectionHeader title={lang === "tr" ? "Bu yazıya atıf" : "Cite this essay"} />
          ) : (
            <SectionHeader title={t.sources} hint={t.sourcesHint} />
          )}
          {sources.length > 0 && (
            <ul id="art-sources" className="divide-y divide-border border-t border-border">
              {sources.map((s) => (
                <li
                  key={s.url}
                  className="py-3 flex flex-col md:flex-row md:items-baseline md:gap-6"
                >
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="font-serif text-base md:text-lg leading-snug hover:text-clay transition-colors flex-1"
                  >
                    {s.title}
                    <span aria-hidden className="text-clay ml-1.5">↗</span>
                  </a>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-ink/55 mt-1 md:mt-0">
                    {s.host}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {citation && (
            <CiteButton
              lang={lang}
              title={citation.title}
              slug={citation.slug}
              isoDate={citation.isoDate}
            />
          )}
        </section>
      )}

      {/* --- What you'll learn --- */}
      {learning.length > 0 && (
        <section aria-labelledby="art-learning">
          <div className="border border-clay/40 bg-clay/5 px-5 py-5 md:px-7 md:py-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-clay font-bold mb-3">
              {t.learning}
            </div>
            <p className="text-sm text-ink/70 leading-relaxed mb-4 max-w-2xl">
              {t.learningHint}
            </p>
            <ul id="art-learning" className="space-y-2.5 list-none pl-0">
              {learning.map((q, i) => (
                <li
                  key={i}
                  className="relative pl-7 text-base md:text-lg font-serif text-ink/90 leading-snug"
                >
                  <span
                    aria-hidden
                    className="absolute left-0 top-[0.15em] text-clay font-bold"
                  >
                    ✓
                  </span>
                  {q}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* --- FAQ --- */}
      {faq.length > 0 && (
        <section aria-labelledby="art-faq">
          <SectionHeader title={t.faq} />
          <div id="art-faq" className="divide-y divide-border border-t border-border">
            {faq.slice(0, 4).map((f, i) => (
              <details
                key={i}
                className="group py-4"
                {...(i === 0 ? { open: true } : {})}
              >
                <summary className="flex items-baseline justify-between gap-4 cursor-pointer list-none">
                  <h3 className="font-serif text-lg md:text-xl leading-snug text-balance group-open:text-clay transition-colors">
                    {f.question}
                  </h3>
                  <span
                    aria-hidden
                    className="text-clay text-xl leading-none transition-transform group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>
                <p className="mt-3 text-base leading-relaxed text-ink/80 max-w-2xl">
                  {f.answer}
                </p>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
